import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import type {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {ViewType} from "../../view_type/ViewType";
import {RegistrationFlowForCurrentUser, startRegistrationFlowForCurrentUser} from "../actions/start_registration_flow_for_current_user";
import {IStartRegistrationFlowPayload, startRegistrationFlow} from "../state/registration_flow_slice";
import {RegistrationWizardAccountEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {isRegistrationFlowHostMounted} from "../utils/registration_flow_host";
import {useUnlockChoiceExperiment} from "./use_unlock_choice_experiment";

type UnlockChoiceLeadCtaRequest = {kind: "lead"; payload: MainLeadDataPayload; gtmClickText: string; gtmSourceSection: string};

type UnlockChoiceContentCtaRequest =
    | {kind: "floor_plan"; propertyId?: number}
    | {kind: "vendor_report"; vendorId: number; offerId?: number; propertyId?: number; amplitudePlacement?: AmplitudeAuthPlacement}
    | {kind: "construction_photos"; hasContent: boolean};

export type UnlockChoiceCtaRequest = UnlockChoiceLeadCtaRequest | UnlockChoiceContentCtaRequest;

export interface IUseUnlockChoiceCta {
    /** Side effect free: true when `startUnlockChoice` called with the same request would take over the click */
    isInterceptable: (request: UnlockChoiceCtaRequest) => boolean;
    /**
     * Returns true when the click was taken over and the caller must skip its own click behaviour.
     * A lead click starts a flow that waits for the experiments: the unlock choice comes only in its treatment, and outside the
     * property page also only in the registration wizard treatment; every other case gets the lead form.
     */
    startUnlockChoice: (request: UnlockChoiceCtaRequest) => boolean;
}

interface IUnlockChoiceCtaContext {
    viewType: IRootState["viewType"]["current"];
    pageProperty: IRootState["property"]["property"];
    isVerifiedUser: boolean;
}

const getLeadFlow = (request: UnlockChoiceLeadCtaRequest, context: IUnlockChoiceCtaContext): RegistrationFlowForCurrentUser | null => {
    const {payload, gtmClickText, gtmSourceSection} = request;
    const {viewType, isVerifiedUser} = context;
    const isVendorLead = payload.offerId === null;

    if (!payload.vendorId || (isVendorLead && isVerifiedUser) || !isRegistrationFlowHostMounted()) {
        return null;
    }

    return {
        userSource: UserSource.APPLICATION,
        gtmClickText,
        entryCtaLabel: gtmClickText,
        gtmSourceSection,
        wizardEntry: viewType === ViewType.PROPERTY ? UnlockChoiceEntryPoint.LEAD : RegistrationWizardAccountEntry.LEAD_INQUIRY,
        modalVariant: "hidden_prices_variant_b",
        propertyId: payload.propertyId ?? undefined,
        offerId: payload.offerId ?? undefined,
        vendorId: payload.vendorId,
        leadSource: payload.source,
        leadSourceSection: payload.sourceSection,
        amplitudePlacement: payload.amplitudePlacement,
        unlockChoiceEntryPoint: UnlockChoiceEntryPoint.LEAD,
        unlockChoiceOrigin: "cta",
        unlockChoiceLeadPayload: payload
    };
};

const getVerifiedContentFlow = (
    isVerifiedUser: boolean,
    unlockChoiceEntryPoint: UnlockChoiceEntryPoint,
    ids: {propertyId?: number; offerId?: number; vendorId?: number},
    amplitudePlacement: AmplitudeAuthPlacement | undefined
): IStartRegistrationFlowPayload | null => {
    if (!isVerifiedUser || ids.offerId === undefined || !ids.vendorId) {
        return null;
    }

    return {
        isUserLoggedIn: true,
        hasCompleteProfile: true,
        hasPhoneVerified: true,
        unlockChoiceEntryPoint,
        unlockChoiceOrigin: "cta",
        propertyId: ids.propertyId,
        offerId: ids.offerId,
        vendorId: ids.vendorId,
        amplitudePlacement
    };
};

const getContentFlow = (request: UnlockChoiceContentCtaRequest, context: IUnlockChoiceCtaContext): IStartRegistrationFlowPayload | null => {
    const {viewType, pageProperty, isVerifiedUser} = context;

    if (viewType !== ViewType.PROPERTY) {
        return null;
    }

    switch (request.kind) {
        case "floor_plan":
            if (!pageProperty || !pageProperty.plan || request.propertyId !== pageProperty.id) {
                return null;
            }

            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.FLOOR_PLAN,
                {
                    propertyId: pageProperty.id,
                    offerId: pageProperty.offer.id,
                    vendorId: pageProperty.offer.vendor.id
                },
                "rp_download_floor_plan"
            );
        case "vendor_report":
            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.VENDOR_REPORT,
                {
                    propertyId: request.propertyId,
                    offerId: request.offerId,
                    vendorId: request.vendorId
                },
                request.amplitudePlacement
            );
        case "construction_photos":
            if (!pageProperty || !request.hasContent) {
                return null;
            }

            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS,
                {
                    propertyId: pageProperty.id,
                    offerId: pageProperty.offer.id,
                    vendorId: pageProperty.offer.vendor.id
                },
                "rp_construction_photos"
            );
        default:
            return null;
    }
};

export const useUnlockChoiceCta = (): IUseUnlockChoiceCta => {
    const dispatch = useAppDispatch();
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {isResolved, isAssigned, isTreatment, trackExposure} = useUnlockChoiceExperiment({isEnabled: true});

    const context: IUnlockChoiceCtaContext = {viewType, pageProperty, isVerifiedUser: isUserLoggedIn && hasCompleteProfile && hasPhoneVerified};
    const isVariantDecided = isResolved && isAssigned;

    const isInterceptable = (request: UnlockChoiceCtaRequest) => {
        if (request.kind === "lead") {
            return getLeadFlow(request, context) !== null;
        }

        return isVariantDecided && isTreatment && getContentFlow(request, context) !== null;
    };

    const startLeadFlow = (request: UnlockChoiceLeadCtaRequest) => {
        const flow = getLeadFlow(request, context);

        if (flow === null) {
            return false;
        }

        dispatch(startRegistrationFlowForCurrentUser(flow, {type: "lead_modal", payload: request.payload}));

        return true;
    };

    const startUnlockChoice = (request: UnlockChoiceCtaRequest) => {
        if (request.kind === "lead") {
            return startLeadFlow(request);
        }

        if (!isVariantDecided) {
            return false;
        }

        const flow = getContentFlow(request, context);

        if (flow === null) {
            return false;
        }

        trackExposure();

        if (!isTreatment) {
            return false;
        }

        dispatch(startRegistrationFlow(flow));

        return true;
    };

    return {isInterceptable, startUnlockChoice};
};
import {MainLeadDataPayload} from "../../application/types/lead/MainLeadDataPayload";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import type {IRootState} from "../../store/store";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {AmplitudeAuthPlacement} from "../../tracking/types/AmplitudeAuthPlacement";
import {ViewType} from "../../view_type/ViewType";
import {RegistrationFlowForCurrentUser, startRegistrationFlowForCurrentUser} from "../actions/start_registration_flow_for_current_user";
import {IStartRegistrationFlowPayload, startRegistrationFlow} from "../state/registration_flow_slice";
import {RegistrationWizardAccountEntry} from "../types/RegistrationWizardEntry";
import {UnlockChoiceEntryPoint} from "../types/UnlockChoiceEntryPoint";
import {isRegistrationFlowHostMounted} from "../utils/registration_flow_host";
import {useUnlockChoiceExperiment} from "./use_unlock_choice_experiment";

type UnlockChoiceLeadCtaRequest = {kind: "lead"; payload: MainLeadDataPayload; gtmClickText: string; gtmSourceSection: string};

type UnlockChoiceContentCtaRequest =
    | {kind: "floor_plan"; propertyId?: number}
    | {kind: "vendor_report"; vendorId: number; offerId?: number; propertyId?: number; amplitudePlacement?: AmplitudeAuthPlacement}
    | {kind: "construction_photos"; hasContent: boolean};

export type UnlockChoiceCtaRequest = UnlockChoiceLeadCtaRequest | UnlockChoiceContentCtaRequest;

export interface IUseUnlockChoiceCta {
    /** Side effect free: true when `startUnlockChoice` called with the same request would take over the click */
    isInterceptable: (request: UnlockChoiceCtaRequest) => boolean;
    /**
     * Returns true when the click was taken over and the caller must skip its own click behaviour.
     * A lead click starts a flow that waits for the experiments: the unlock choice comes only in its treatment, and outside the
     * property page also only in the registration wizard treatment; every other case gets the lead form.
     */
    startUnlockChoice: (request: UnlockChoiceCtaRequest) => boolean;
}

interface IUnlockChoiceCtaContext {
    viewType: IRootState["viewType"]["current"];
    pageProperty: IRootState["property"]["property"];
    isVerifiedUser: boolean;
}

const getLeadFlow = (request: UnlockChoiceLeadCtaRequest, context: IUnlockChoiceCtaContext): RegistrationFlowForCurrentUser | null => {
    const {payload, gtmClickText, gtmSourceSection} = request;
    const {viewType, isVerifiedUser} = context;
    const isVendorLead = payload.offerId === null;

    if (!payload.vendorId || (isVendorLead && isVerifiedUser) || !isRegistrationFlowHostMounted()) {
        return null;
    }

    return {
        userSource: UserSource.APPLICATION,
        gtmClickText,
        entryCtaLabel: gtmClickText,
        gtmSourceSection,
        wizardEntry: viewType === ViewType.PROPERTY ? UnlockChoiceEntryPoint.LEAD : RegistrationWizardAccountEntry.LEAD_INQUIRY,
        modalVariant: "hidden_prices_variant_b",
        propertyId: payload.propertyId ?? undefined,
        offerId: payload.offerId ?? undefined,
        vendorId: payload.vendorId,
        leadSource: payload.source,
        leadSourceSection: payload.sourceSection,
        amplitudePlacement: payload.amplitudePlacement,
        unlockChoiceEntryPoint: UnlockChoiceEntryPoint.LEAD,
        unlockChoiceOrigin: "cta",
        unlockChoiceLeadPayload: payload
    };
};

const getVerifiedContentFlow = (
    isVerifiedUser: boolean,
    unlockChoiceEntryPoint: UnlockChoiceEntryPoint,
    ids: {propertyId?: number; offerId?: number; vendorId?: number},
    amplitudePlacement: AmplitudeAuthPlacement | undefined
): IStartRegistrationFlowPayload | null => {
    if (!isVerifiedUser || ids.offerId === undefined || !ids.vendorId) {
        return null;
    }

    return {
        isUserLoggedIn: true,
        hasCompleteProfile: true,
        hasPhoneVerified: true,
        unlockChoiceEntryPoint,
        unlockChoiceOrigin: "cta",
        propertyId: ids.propertyId,
        offerId: ids.offerId,
        vendorId: ids.vendorId,
        amplitudePlacement
    };
};

const getContentFlow = (request: UnlockChoiceContentCtaRequest, context: IUnlockChoiceCtaContext): IStartRegistrationFlowPayload | null => {
    const {viewType, pageProperty, isVerifiedUser} = context;

    if (viewType !== ViewType.PROPERTY) {
        return null;
    }

    switch (request.kind) {
        case "floor_plan":
            if (!pageProperty || !pageProperty.plan || request.propertyId !== pageProperty.id) {
                return null;
            }

            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.FLOOR_PLAN,
                {
                    propertyId: pageProperty.id,
                    offerId: pageProperty.offer.id,
                    vendorId: pageProperty.offer.vendor.id
                },
                "rp_download_floor_plan"
            );
        case "vendor_report":
            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.VENDOR_REPORT,
                {
                    propertyId: request.propertyId,
                    offerId: request.offerId,
                    vendorId: request.vendorId
                },
                request.amplitudePlacement
            );
        case "construction_photos":
            if (!pageProperty || !request.hasContent) {
                return null;
            }

            return getVerifiedContentFlow(
                isVerifiedUser,
                UnlockChoiceEntryPoint.CONSTRUCTION_PHOTOS,
                {
                    propertyId: pageProperty.id,
                    offerId: pageProperty.offer.id,
                    vendorId: pageProperty.offer.vendor.id
                },
                "rp_construction_photos"
            );
        default:
            return null;
    }
};

export const useUnlockChoiceCta = (): IUseUnlockChoiceCta => {
    const dispatch = useAppDispatch();
    const viewType = useAppSelector((state) => state.viewType.current);
    const pageProperty = useAppSelector((state) => state.property.property);
    const {isUserLoggedIn, hasCompleteProfile, hasPhoneVerified} = useShouldHidePrices();
    const {isResolved, isAssigned, isTreatment, trackExposure} = useUnlockChoiceExperiment({isEnabled: true});

    const context: IUnlockChoiceCtaContext = {viewType, pageProperty, isVerifiedUser: isUserLoggedIn && hasCompleteProfile && hasPhoneVerified};
    const isVariantDecided = isResolved && isAssigned;

    const isInterceptable = (request: UnlockChoiceCtaRequest) => {
        if (request.kind === "lead") {
            return getLeadFlow(request, context) !== null;
        }

        return isVariantDecided && isTreatment && getContentFlow(request, context) !== null;
    };

    const startLeadFlow = (request: UnlockChoiceLeadCtaRequest) => {
        const flow = getLeadFlow(request, context);

        if (flow === null) {
            return false;
        }

        dispatch(startRegistrationFlowForCurrentUser(flow, {type: "lead_modal", payload: request.payload}));

        return true;
    };

    const startUnlockChoice = (request: UnlockChoiceCtaRequest) => {
        if (request.kind === "lead") {
            return startLeadFlow(request);
        }

        if (!isVariantDecided) {
            return false;
        }

        const flow = getContentFlow(request, context);

        if (flow === null) {
            return false;
        }

        trackExposure();

        if (!isTreatment) {
            return false;
        }

        dispatch(startRegistrationFlow(flow));

        return true;
    };

    return {isInterceptable, startUnlockChoice};
};
